import _ from 'lodash'
import { FC, useEffect, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'
import { getFormValues, stopSubmit } from 'redux-form'
import { v4 } from 'uuid'

import { RESPONSE } from 'constants/http'
import { FeatureFlags } from 'constants/security'
import { BasicSingleResponse } from 'constants/service'
import { Child } from 'services/child/constants'
import { UPLOAD_DIRECTORY } from 'services/legacy/upload/constants'
import { NurserySettings } from 'services/nurseries/models'
import {
  FormPreviewFormValues,
  RegistrationForm,
  RegistrationFormBody,
  RegistrationFormStatus,
} from 'services/registrationForms/models'

import { getBackendErrors } from 'utils/backendErrors'
import { generateRoute } from 'utils/routing'

import { withAuthenticationService, withAuthenticationServiceProps } from 'services/authentication'
import { withFormBuilderSettingsService, withFormBuilderSettingsServiceProps } from 'services/formBuilderSettings'
import formBuilderSettingsSelectors from 'services/formBuilderSettings/selectors'
import { withChildService, withChildServiceProps } from 'services/legacy/child'
import * as childSelectors from 'services/legacy/child/selectors'
import { withUploadService, withUploadServiceProps } from 'services/legacy/upload'
import { withNurseryContextService, withNurseryContextServiceProps } from 'services/nurseryContext'
import { withRegistrationFormsService, withRegistrationFormsServiceProps } from 'services/registrationForms'
import * as registrationFormsSelectors from 'services/registrationForms/selectors'
import { withRouter, withRouterProps } from 'services/router'
import * as subdomainSelectors from 'services/subdomain/selectors'
import { withModalService, withModalServiceProps } from 'services/utils/modal'
import { withSnackbarService, withSnackbarServiceProps } from 'services/utils/snackbar'

import { SelectedImage } from 'components/PhotoUpload/PhotoUpload'
import { ModalType } from 'modals'

import { RootState } from 'core/reducers'
import i18n from 'translations'

import { FORM_PREVIEW_FORM } from './components/FormPreview/FormPreview'
import RegistrationFormView from './RegistrationFormView'

const CHILD_GROUPS = {
  read: [
    'nurseryClass',
    'user',
    'carer',
    'carer.details',
    'carer.address',
    'carerChildRelation.carer',
    'carerChildRelation.details',
    'user.details',
    'child',
    'child.nursery',
    'nursery',
    'nursery.settings',
    'nursery.nurserySettings',
    'nurserySettings',
    'child.extend',
    'child.ethnicity',
    'child.keyWorkers',
    'child.nurseryClass',
    'child.carerChildRelations',
    'childProfile',
    'childInformation',
    'ethnicity',
  ],
}

const REGISTRATION_FORM_GROUPS = {
  read: [
    'medium',
    'ethnicity',
    'child.ethnicity',
  ],
}

const mapState = (state: RootState) => ({
  child: childSelectors.getChildSelector(state),
  formValues: getFormValues(FORM_PREVIEW_FORM)(state) as FormPreviewFormValues,
  nurseryFormBuilderSettings: formBuilderSettingsSelectors.getSettings(state),
  registrationForm: registrationFormsSelectors.getRegistrationForms(state),
  subdomain: subdomainSelectors.getSubdomainInfo(state),
})

const mapDispatch = {
  injectValidation: (data) => stopSubmit(FORM_PREVIEW_FORM, data),
}

const connector = connect(mapState, mapDispatch)

type PropsFromRedux = ConnectedProps<typeof connector>

type RegistrationFormFullProps = PropsFromRedux
  & withNurseryContextServiceProps
  & withRegistrationFormsServiceProps
  & withAuthenticationServiceProps
  & withFormBuilderSettingsServiceProps
  & withRouterProps
  & withUploadServiceProps
  & withChildServiceProps
  & withSnackbarServiceProps
  & withModalServiceProps

const RegistrationFormContainer: FC<RegistrationFormFullProps> = ({
  authenticationActions,
  child,
  childActions,
  formBuilderSettingsActions,
  formValues,
  injectValidation,
  location,
  modalActions,
  nurseryFormBuilderSettings,
  params,
  registrationForm,
  registrationFormsActions,
  snackbarActions,
  subdomain,
  uploadActions,
}) => {
  const [nurserySettings, setNurserySettings] = useState<NurserySettings>(null)
  const [isFinanceV3Enabled, setIsFinanceV3Enabled] = useState<boolean>(null)
  const [initialValues, setInitialValues] = useState<FormPreviewFormValues>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isSubmittingAsCompleted, setIsSubmittingAsCompleted] = useState<boolean>(false)
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState<boolean>(false)
  const { query } = location
  const { t: token } = query

  const handleGetImage = async (url: string): Promise<SelectedImage> => {
    const response = await fetch(url, {
      credentials: 'include',
      mode: 'cors',
    })
    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)

    const image = new Image()
    image.src = objectUrl

    return {
      file: new File([blob], 'signature.png', { type: 'image/png' }),
      isUploaded: true,
      value: objectUrl,
    }
  }

  const getInitialValues = async () => {
    const iValues = await registrationFormsSelectors.getInitialValues({
      child,
      nurseryFormBuilderSettings,
      onGetImage: handleGetImage,
      registrationForm,
    })

    setInitialValues(iValues)
  }

  useEffect(() => {
    if (null === child || null === nurseryFormBuilderSettings || null === registrationForm) {
      return
    }

    getInitialValues()
  }, [child, nurseryFormBuilderSettings, registrationForm])

  useEffect(() => {
    if (!token) {
      window.location.href = generateRoute('VARIOUS.NOT_FOUND')
    }
  }, [])

  const getAvailableRegistrationForms = (response, childResponse: BasicSingleResponse<Child>) => {
    const { data: { featureFlags, nursery } } = response
    const { data: { nursery: childNursery } } = childResponse

    setNurserySettings(childNursery?.nurserySettings || {} as NurserySettings)

    if (!_.includes(featureFlags, FeatureFlags.REGISTRATION_FORMS)) {
      window.location.href = generateRoute('VARIOUS.NOT_FOUND')
    }

    formBuilderSettingsActions.getAvailable({
      onSuccess: () => {
        setIsLoading(false)
      },
      params: [nursery.id, token],
    })
  }

  const getChild = (response: RegistrationForm, authResponse) => {
    setIsFinanceV3Enabled(authResponse?.data?.nursery?.newFinanceEnabled || false)

    childActions.get({
      onFailed: () => {
        window.location.href = generateRoute('VARIOUS.NOT_FOUND')
      },
      onSuccess: (childResponse) => getAvailableRegistrationForms(authResponse, childResponse),
      params: [response.child.id, { groups: CHILD_GROUPS }, token],
    })
  }

  const getAuthMe = (registrationFormResponse: BasicSingleResponse<RegistrationForm>) => (
    authenticationActions.authMe({
      onSuccess: (authResponse) => getChild(registrationFormResponse.data, authResponse),
      params: [token],
    })
  )

  useEffect(() => {
    if (params?.id) {
      registrationFormsActions.get({
        onFailed: (response) => {
          if (response.code === RESPONSE.HTTP_404_NOT_FOUND) {
            window.location.href = generateRoute('VARIOUS.NOT_FOUND')

            return
          }

          if (response.message) {
            modalActions.show<ModalType.ALERT>(ModalType.ALERT, {
              icon: 'warning',
              text: response.message,
            })
          }
        },
        onSuccess: getAuthMe,
        params: [params.id, { groups: REGISTRATION_FORM_GROUPS }, token],
      })
    }
  }, [params?.id])

  const handleUploadImage = (selectedImage: SelectedImage): Promise<string> => (
    uploadActions.uploadFile(
      selectedImage.file,
      v4(),
      UPLOAD_DIRECTORY.IMAGES,
      true,
      token,
    ) as unknown as Promise<string>
  )

  const handleSubmitSuccess = (status: RegistrationFormStatus) => {
    if (status === RegistrationFormStatus.SENT) {
      setIsSubmitting(false)
      snackbarActions.show({
        message: i18n.t('module:RegistrationForm:savedAsDraft'),
      })
    }

    if (status === RegistrationFormStatus.COMPLETED) {
      setIsSubmittedSuccessfully(true)
    }
  }

  const handleSubmitFailed = (response, body: RegistrationFormBody) => {
    setIsSubmitting(false)
    setIsSubmittingAsCompleted(false)

    const errors = getBackendErrors(response)

    if (!errors) {
      return
    }

    if (errors?.data?.carerChildRelations?.length) {
      const fullArray = _.get(formValues, 'data.carerChildRelations', [])
      const sentArray = _.get(body, 'data.carerChildRelations', [])
      const errorArray = _.get(errors, 'data.carerChildRelations', [])

      const mappedErrors = []

      _.forEach(errorArray, (errorItem, originalIndex) => {
        const objectId = _.get(sentArray[originalIndex], 'objectId')
        const sentIndex = _.findIndex(fullArray, { objectId })

        if (null !== sentIndex) {
          mappedErrors[sentIndex] = errorItem
        }
      })

      errors.data.carerChildRelations = mappedErrors
    }

    injectValidation(errors)
  }

  const handleSubmitConfirmed = async (values: FormPreviewFormValues, status: RegistrationFormStatus) => {
    const body = await registrationFormsSelectors.getBody({
      child,
      nurseryFormBuilderSettings,
      onUploadImage: handleUploadImage,
      status,
      values,
    })

    if (!body.data) {
      snackbarActions.show({
        message: i18n.t('module:RegistrationForm:noChanges'),
      })

      setIsSubmitting(false)
      setIsSubmittingAsCompleted(false)

      return
    }

    registrationFormsActions.update({
      body,
      onFailed: (response) => handleSubmitFailed(response, body),
      onSuccess: () => handleSubmitSuccess(status),
      params: [registrationForm.id, token],
    })
  }

  const handleSubmit = async (values: FormPreviewFormValues, status: RegistrationFormStatus) => {
    if (status === RegistrationFormStatus.COMPLETED) {
      return modalActions.show<ModalType.SUBMIT_REGISTRATION_FORM>(ModalType.SUBMIT_REGISTRATION_FORM, {
        onSubmit: () => {
          setIsSubmitting(true)
          setIsSubmittingAsCompleted(true)

          return handleSubmitConfirmed(values, status)
        },
      })
    }

    setIsSubmitting(true)

    return handleSubmitConfirmed(values, status)
  }

  return (
    <RegistrationFormView
      child={child}
      initialValues={initialValues}
      isFinanceV3Enabled={isFinanceV3Enabled}
      isLoading={isLoading}
      isSubmittedSuccessfully={isSubmittedSuccessfully}
      isSubmitting={isSubmitting}
      isSubmittingAsCompleted={isSubmittingAsCompleted}
      nurseryFormBuilderSettings={nurseryFormBuilderSettings}
      nurserySettings={nurserySettings}
      registrationForm={registrationForm}
      subdomain={subdomain}
      onSubmit={handleSubmit}
    />
  )
}

const enhance = compose(
  withRegistrationFormsService,
  withRouter,
  withNurseryContextService,
  withAuthenticationService,
  withFormBuilderSettingsService,
  withUploadService,
  withChildService,
  withSnackbarService,
  withModalService,
  connector,
)

export default enhance(RegistrationFormContainer)
